import { ApiError, errorMessage } from '@runnerboy/api-client';
import type { AddressDto, AuthUserDto, GeoPoint } from '@runnerboy/types';
import { Alert, Badge, Button, Checkbox, cx, Field, Input, Sheet, Textarea, useToast } from '@runnerboy/ui';
import { Building2, Crosshair, Hotel, Loader2, MapPin, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { client, useSession } from '../lib/session';
import { currentPosition } from '../lib/native';

// ---- Login (mobile OTP) -------------------------------------------------------------------------------------
export function LoginSheet() {
  const { t } = useTranslation();
  const { loginOpen, setLoginOpen } = useSession();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testMode, setTestMode] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // A fresh sheet never shows the previous attempt's error.
  useEffect(() => {
    if (loginOpen) setError(null);
  }, [loginOpen]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.requestOtp(phone);
      setTestMode(!!r.testMode);
      setStep('code');
      setResendIn(r.resendAfterSec);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.verifyOtp(phone, code);
      setLoginOpen(false);
      setStep('phone');
      setCode('');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={loginOpen} onClose={() => setLoginOpen(false)} title={t('auth.loginTitle')}>
      <p className="text-sm text-ink-500">{t('auth.loginSubtitle')}</p>
      <form
        className="mt-5 flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void (step === 'phone' ? send() : verify());
        }}
      >
        {step === 'phone' ? (
          <Field label={t('auth.phoneLabel')} htmlFor="phone">
            <div className="flex gap-2">
              <span className="grid h-12 place-items-center rounded-[12px] border border-ink-200 bg-ink-50 px-3 text-sm font-semibold text-ink-700">+91</span>
              <Input id="phone" inputMode="numeric" autoComplete="tel-national" placeholder={t('auth.phonePlaceholder')} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} autoFocus />
            </div>
          </Field>
        ) : (
          <Field label={t('auth.codeLabel')} htmlFor="otp" hint={t('auth.codeSentTo', { phone: `+91 ${phone}` })}>
            <Input id="otp" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus className="rb-tabular tracking-[0.4em]" />
          </Field>
        )}
        {testMode && <Alert tone="warning">{t('auth.testModeHint')}</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" size="lg" block loading={busy} disabled={step === 'phone' ? phone.length !== 10 : code.length !== 6}>
          {step === 'phone' ? t('auth.sendCode') : t('auth.verify')}
        </Button>
        {step === 'code' && (
          <button type="button" className="text-sm font-semibold text-peacock-700 disabled:text-ink-400" disabled={resendIn > 0} onClick={() => void send()}>
            {resendIn > 0 ? t('auth.resendIn', { seconds: resendIn }) : t('auth.resend')}
          </button>
        )}
        <p className="text-center text-xs text-ink-500">{t('auth.terms')}</p>
      </form>
    </Sheet>
  );
}

// ---- Age gate --------------------------------------------------------------------------------------------
export function AgeGateSheet({ open, onDone }: { open: boolean; onDone: () => void }) {
  const { t } = useTranslation();
  const { setAgeGate } = useSession();
  return (
    <Sheet open={open} onClose={onDone} title={t('ageGate.title')}>
      <div className="flex items-start gap-3">
        <Badge tone="dark" className="mt-1 text-sm">
          21+
        </Badge>
        <p className="text-sm leading-relaxed text-ink-600">{t('ageGate.body')}</p>
      </div>
      <div className="mt-6 flex flex-col gap-3">
        <Button
          size="lg"
          block
          onClick={() => {
            setAgeGate('confirmed');
            onDone();
          }}
        >
          {t('ageGate.confirm')}
        </Button>
        <Button
          size="lg"
          variant="outline"
          block
          onClick={() => {
            setAgeGate('declined');
            onDone();
          }}
        >
          {t('ageGate.decline')}
        </Button>
      </div>
      <p className="mt-4 text-center text-xs text-ink-500">{t('responsible.notice')}</p>
    </Sheet>
  );
}

// ---- Pin picker (MapLibre with keyboard/numeric fallback) ------------------------------------------------
export function PinPicker({ value, onChange, styleUrl }: { value: GeoPoint; onChange: (p: GeoPoint) => void; styleUrl: string }) {
  const { t } = useTranslation();
  const el = useRef<HTMLDivElement>(null);
  const [fallback, setFallback] = useState(false);
  const mapRef = useRef<import('maplibre-gl').Map | null>(null);
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => !cancelled && !mapRef.current?.isStyleLoaded() && setFallback(true), 8000);
    void import('maplibre-gl')
      .then((mod) => {
        if (cancelled || !el.current) return;
        const lib = (mod as unknown as { default?: typeof import('maplibre-gl') }).default ?? mod;
        const map = new lib.Map({ container: el.current, style: styleUrl, center: [value.lng, value.lat], zoom: 16, attributionControl: { compact: true } });
        map.on('moveend', () => {
          const c = map.getCenter();
          cb.current({ lat: Number(c.lat.toFixed(6)), lng: Number(c.lng.toFixed(6)) });
        });
        map.on('load', () => clearTimeout(timer));
        mapRef.current = map;
      })
      .catch(() => setFallback(true));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleUrl]);
  useEffect(() => {
    const m = mapRef.current;
    if (m && (Math.abs(m.getCenter().lat - value.lat) > 1e-5 || Math.abs(m.getCenter().lng - value.lng) > 1e-5)) m.jumpTo({ center: [value.lng, value.lat] });
  }, [value.lat, value.lng]);

  return (
    <div>
      <div className="relative h-56 overflow-hidden rounded-[16px] bg-peacock-50 ring-1 ring-ink-100">
        {!fallback && <div ref={el} className="absolute inset-0" />}
        {fallback && <div className="grid h-full place-items-center px-6 text-center text-sm text-ink-500">Map unavailable — adjust the coordinates below.</div>}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full">
          <MapPin className="h-10 w-10 fill-peacock-600 text-white drop-shadow-lg" aria-hidden />
        </div>
        <div className="pointer-events-none absolute inset-x-3 bottom-3 rounded-[12px] bg-ink-900/85 px-3 py-2 text-center text-xs font-semibold text-white">{t('location.movePin')}</div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Input aria-label="Latitude" inputMode="decimal" value={value.lat} onChange={(e) => Number.isFinite(Number(e.target.value)) && onChange({ ...value, lat: Number(e.target.value) })} className="h-10 text-sm" />
        <Input aria-label="Longitude" inputMode="decimal" value={value.lng} onChange={(e) => Number.isFinite(Number(e.target.value)) && onChange({ ...value, lng: Number(e.target.value) })} className="h-10 text-sm" />
      </div>
    </div>
  );
}

// ---- Location chooser -------------------------------------------------------------------------------------
interface GeocodeHit {
  location: GeoPoint;
  formattedAddress: string;
  locality: string | null;
  city: string | null;
  pincode: string | null;
}

export function LocationSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { setLocation, user, config, availability, location } = useSession();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<GeocodeHit[]>([]);
  const [addresses, setAddresses] = useState<AddressDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [pin, setPin] = useState<GeoPoint | null>(null);
  const [addressOpen, setAddressOpen] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    client.get<AddressDto[]>('/me/addresses').then(setAddresses).catch(() => setAddresses([]));
  }, [open, user]);

  useEffect(() => {
    if (q.trim().length < 2) return setHits([]);
    const id = setTimeout(() => {
      client.get<GeocodeHit[]>(`/public/geocode?q=${encodeURIComponent(q)}`).then(setHits).catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const useGps = () => {
    setGpsError(null);
    setBusy(true);
    currentPosition()
      .then((pos) => setPin({ lat: Number(pos.lat.toFixed(6)), lng: Number(pos.lng.toFixed(6)) }))
      .catch(() => setGpsError(t('location.gpsDenied')))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <Sheet open={open && !addressOpen} onClose={onClose} title={t('location.choose')}>
        {!pin ? (
          <div className="flex flex-col gap-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-400" aria-hidden />
              <Input autoFocus placeholder={t('location.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} className="pl-11" aria-label={t('location.searchPlaceholder')} />
            </div>
            <Button variant="outline" onClick={useGps} loading={busy} className="justify-start text-peacock-700">
              <Crosshair className="h-5 w-5" aria-hidden /> {t('location.useCurrent')}
            </Button>
            {gpsError && <Alert tone="warning">{gpsError}</Alert>}
            {hits.length > 0 && (
              <ul className="divide-y divide-ink-100 rounded-[14px] ring-1 ring-ink-100">
                {hits.map((h) => (
                  <li key={h.formattedAddress}>
                    <button className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-ink-50" onClick={() => setPin(h.location)}>
                      <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-ink-400" aria-hidden />
                      <span>
                        <span className="block text-sm font-semibold text-ink-900">{h.locality ?? h.city}</span>
                        <span className="block text-xs text-ink-500">{h.formattedAddress}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {addresses.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-bold uppercase tracking-wide text-ink-500">{t('location.savedAddresses')}</div>
                <ul className="flex flex-col gap-2">
                  {addresses.map((a) => (
                    <li key={a.id}>
                      <button
                        className={cx('flex w-full items-start gap-3 rounded-[14px] px-4 py-3 text-left ring-1', location?.addressId === a.id ? 'bg-peacock-50 ring-peacock-200' : 'ring-ink-100 hover:bg-ink-50')}
                        onClick={() => {
                          setLocation({ lat: a.location.lat, lng: a.location.lng, label: `${t(`address.${a.label}`)} · ${a.locality}`, addressId: a.id });
                          onClose();
                        }}
                      >
                        {a.label === 'HOTEL' ? <Hotel className="mt-0.5 h-5 w-5 text-ink-400" /> : <Building2 className="mt-0.5 h-5 w-5 text-ink-400" />}
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-ink-900">{t(`address.${a.label}`)}</span>
                          <span className="block truncate text-xs text-ink-500">{[a.flat, a.building, a.locality, a.city].filter(Boolean).join(', ')}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <PinPicker value={pin} onChange={setPin} styleUrl={config?.mapStyleUrl ?? 'https://demotiles.maplibre.org/style.json'} />
            <Button
              size="lg"
              block
              onClick={() => {
                // Never show raw coordinates: label with the search text or a reverse-geocoded locality.
                const point = { lat: pin.lat, lng: pin.lng, addressId: null };
                setLocation({ ...point, label: q || t('location.pinned', 'Pinned location') });
                if (!q)
                  void client
                    .get<GeocodeHit | null>(`/public/reverse-geocode?lat=${pin.lat}&lng=${pin.lng}`)
                    .then((h) => h && (h.locality || h.city) && setLocation({ ...point, label: [h.locality, h.city].filter(Boolean).join(', ') }))
                    .catch(() => undefined);
                if (user) setAddressOpen(true);
                else onClose();
              }}
            >
              {t('location.confirmPin')}
            </Button>
            <Button variant="ghost" onClick={() => setPin(null)}>
              {t('common.back')}
            </Button>
          </div>
        )}
        {availability && !availability.resolved && <Alert tone="warning" className="mt-4">{t('location.notAvailable')}</Alert>}
      </Sheet>
      {pin && (
        <AddressSheet
          open={addressOpen}
          point={pin}
          onClose={() => {
            setAddressOpen(false);
            onClose();
          }}
        />
      )}
    </>
  );
}

// ---- Address details --------------------------------------------------------------------------------------
type Label = 'HOME' | 'WORK' | 'HOTEL' | 'OTHER';

export function AddressSheet({ open, onClose, point }: { open: boolean; onClose: () => void; point: GeoPoint }) {
  const { t } = useTranslation();
  const { setLocation } = useSession();
  const toast = useToast();
  const [form, setForm] = useState({ label: 'HOME' as Label, flat: '', floor: '', building: '', street: '', locality: '', landmark: '', city: '', pincode: '', instructions: '', hotelName: '', roomNumber: '', receptionInstructions: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!open) return;
    client
      .get<{ locality: string | null; city: string | null; pincode: string | null } | null>(`/public/reverse-geocode?lat=${point.lat}&lng=${point.lng}`)
      .then((r) => r && setForm((f) => ({ ...f, locality: f.locality || r.locality || '', city: f.city || r.city || '', pincode: f.pincode || r.pincode || '' })))
      .catch(() => undefined);
  }, [open, point.lat, point.lng]);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      const body = Object.fromEntries(Object.entries({ ...form, lat: point.lat, lng: point.lng }).map(([k, v]) => [k, v === '' ? null : v]));
      const a = await client.post<AddressDto & { lat: number; lng: number }>('/me/addresses', { ...body, label: form.label, flat: form.flat, locality: form.locality, city: form.city, pincode: form.pincode, isDefault: true });
      setLocation({ lat: point.lat, lng: point.lng, label: `${t(`address.${form.label}`)} · ${form.locality}`, addressId: a.id });
      toast('success', t('address.save'));
      onClose();
    } catch (e) {
      if (e instanceof ApiError && Array.isArray(e.details)) {
        setErrors(Object.fromEntries((e.details as { path: string; message: string }[]).map((d) => [d.path, d.message])));
      }
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('address.title')}
      footer={
        <Button size="lg" block loading={busy} onClick={() => void save()} disabled={!form.flat || !form.locality || !form.city || !/^\d{6}$/.test(form.pincode)}>
          {t('address.save')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div role="radiogroup" aria-label={t('address.label')} className="flex flex-wrap gap-2">
          {(['HOME', 'WORK', 'HOTEL', 'OTHER'] as Label[]).map((l) => (
            <button key={l} role="radio" aria-checked={form.label === l} onClick={() => setForm((f) => ({ ...f, label: l }))} className={cx('h-10 rounded-full px-4 text-sm font-semibold ring-1', form.label === l ? 'bg-peacock-600 text-white ring-peacock-600' : 'bg-white text-ink-700 ring-ink-200')}>
              {t(`address.${l}`)}
            </button>
          ))}
        </div>
        {form.label === 'HOTEL' && (
          <>
            <Field label={t('address.hotelName')} htmlFor="hotel" required>
              <Input id="hotel" value={form.hotelName} onChange={set('hotelName')} />
            </Field>
            <Field label={t('address.roomNumber')} htmlFor="room">
              <Input id="room" value={form.roomNumber} onChange={set('roomNumber')} />
            </Field>
            <Field label={t('address.reception')} htmlFor="reception">
              <Input id="reception" value={form.receptionInstructions} onChange={set('receptionInstructions')} />
            </Field>
            <Alert tone="info">{t('address.hotelNote')}</Alert>
          </>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('address.flat')} htmlFor="flat" required error={errors.flat}>
            <Input id="flat" value={form.flat} onChange={set('flat')} autoComplete="address-line1" />
          </Field>
          <Field label={t('address.floor')} htmlFor="floor">
            <Input id="floor" value={form.floor} onChange={set('floor')} />
          </Field>
        </div>
        <Field label={t('address.building')} htmlFor="building">
          <Input id="building" value={form.building} onChange={set('building')} />
        </Field>
        <Field label={t('address.street')} htmlFor="street">
          <Input id="street" value={form.street} onChange={set('street')} autoComplete="address-line2" />
        </Field>
        <Field label={t('address.locality')} htmlFor="locality" required error={errors.locality}>
          <Input id="locality" value={form.locality} onChange={set('locality')} />
        </Field>
        <Field label={t('address.landmark')} htmlFor="landmark">
          <Input id="landmark" value={form.landmark} onChange={set('landmark')} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('address.city')} htmlFor="city" required>
            <Input id="city" value={form.city} onChange={set('city')} autoComplete="address-level2" />
          </Field>
          <Field label={t('address.pincode')} htmlFor="pincode" required error={errors.pincode}>
            <Input id="pincode" inputMode="numeric" value={form.pincode} onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} autoComplete="postal-code" />
          </Field>
        </div>
        <Field label={t('address.instructions')} htmlFor="instructions">
          <Textarea id="instructions" value={form.instructions} onChange={set('instructions')} maxLength={300} />
        </Field>
        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </Sheet>
  );
}

// ---- Stage A age verification ------------------------------------------------------------------------------
export function AgeVerifySheet({ open, onClose, onVerified }: { open: boolean; onClose: () => void; onVerified: () => void }) {
  const { t } = useTranslation();
  const [dob, setDob] = useState('');
  const [declared, setDeclared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.post('/me/age-verification', { dateOfBirth: dob, declaration: true });
      onVerified();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title={t('checkout.ageTitle')}>
      <p className="text-sm text-ink-600">{t('checkout.ageBody')}</p>
      <div className="mt-4 flex flex-col gap-4">
        <Field label={t('checkout.dob')} htmlFor="dob">
          <Input id="dob" type="date" max={new Date().toISOString().slice(0, 10)} value={dob} onChange={(e) => setDob(e.target.value)} />
        </Field>
        <Checkbox checked={declared} onChange={setDeclared}>
          {t('checkout.declaration')}
        </Checkbox>
        {error && <Alert tone="danger">{error}</Alert>}
        <Button size="lg" block loading={busy} disabled={!dob || !declared} onClick={() => void submit()}>
          {t('checkout.verifyAge')}
        </Button>
      </div>
    </Sheet>
  );
}

export function useMe() {
  const { user } = useSession();
  const [me, setMe] = useState<(AuthUserDto & { ageVerification?: { verified: boolean } }) | null>(null);
  const [loading, setLoading] = useState(false);
  const reload = () => {
    if (!user) return setMe(null);
    setLoading(true);
    client
      .get<AuthUserDto & { ageVerification: { verified: boolean } }>('/me')
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  };
   
  useEffect(reload, [user]);
  return { me, loading, reload };
}

export function InlineLoader() {
  return (
    <div className="flex justify-center py-10 text-ink-400">
      <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
    </div>
  );
}
